import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, GraduationCap, Gift, Sun, Ticket, Sparkles, Users, ArrowRight } from "lucide-react";
import { useSEO } from "../lib/useSEO";
import { PageDecor } from "../components/decor/PageDecor";
import { useReglages } from "../lib/useReglages";
import { SAISON } from "../lib/dates";
import { construireAgenda, aVenir, periodeLisible, TYPES_AGENDA } from "../lib/agenda";
import { SPECTACLES } from "./Spectacles";
import { CalendrierAnnee } from "../components/CalendrierAnnee";

const ICONES = {
  rentree: GraduationCap,
  fermeture: Sun,
  portes_ouvertes: Gift,
  stage: Sparkles,
  spectacle: Ticket,
  audition: Users,
};

export default function DatesImportantes() {
  const { portes_ouvertes: fenetresPO } = useReglages();
  const tous = useMemo(() => construireAgenda(fenetresPO, SPECTACLES), [fenetresPO]);
  const prochains = useMemo(() => aVenir(tous), [tous]);
  const [tout, setTout] = useState(false);
  const liste = tout ? tous : prochains;

  useSEO({
    title: `Dates importantes ${SAISON.libelle} — 1000 ARTS Cugy VD`,
    description: `Toutes les dates clés de la saison ${SAISON.libelle} de 1000 ARTS : rentrée des cours, périodes où l'école est fermée, Portes Ouvertes avec cours d'essai offerts, stages de vacances, spectacles et auditions.`,
    canonical: "/dates-importantes",
  });

  return (
    <div data-testid="page-dates-importantes" className="pt-32 pb-24 relative overflow-hidden mesh-bg">
      <PageDecor />
      <div className="max-w-[1100px] mx-auto px-6 md:px-12 relative z-10">
        <div className="eyebrow mb-3 flex items-center gap-2">
          <CalendarDays size={14} /> Saison {SAISON.libelle}
        </div>
        <h1 className="font-display text-5xl md:text-7xl tracking-tight leading-tight max-w-4xl">
          Dates <span style={{ color: "var(--primary)" }}>importantes</span>
        </h1>
        <p className="mt-6 text-lg md:text-xl text-[var(--muted)] max-w-3xl leading-relaxed">
          L'année de l'école d'un coup d'œil : vacances, Portes Ouvertes, stages, spectacles et
          auditions. Touche un jour pour voir ce qui s'y passe. Cette page se met à jour toute seule.
        </p>

        {prochains.length > 0 && (
          <section className="mt-10" data-testid="dates-prochaines">
            <div className="eyebrow mb-3">Prochainement</div>
            <div className="grid gap-4 md:grid-cols-3">
              {prochains.slice(0, 3).map((e) => {
                const t = TYPES_AGENDA[e.type];
                const Icone = ICONES[e.type] || CalendarDays;
                return (
                  <div key={e.id} className="card-flat flex flex-col" data-testid={`prochaine-${e.id}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                        style={{ background: `${t.couleur}18`, color: t.couleur }} aria-hidden>
                        <Icone size={16} />
                      </span>
                      <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: t.couleur }}>{t.label}</span>
                    </div>
                    <div className="text-sm font-semibold">{periodeLisible(e.debut, e.fin)}</div>
                    <div className="font-display text-lg leading-tight mt-1">{e.titre}</div>
                    {e.lien && (
                      <Link to={e.lien.to} className="inline-flex items-center gap-1 text-sm font-medium mt-auto pt-3"
                        style={{ color: "var(--primary)" }}>
                        {e.lien.label} <ArrowRight size={14} />
                      </Link>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        <section className="mt-12" data-testid="dates-calendrier">
          <div className="eyebrow mb-3">L'année {SAISON.libelle} en un coup d'œil</div>
          <CalendrierAnnee testId="site-annee" variante="site" />
        </section>

        <details className="mt-12 group" data-testid="dates-liste">
          <summary className="cursor-pointer select-none font-display text-xl list-none flex items-center gap-2">
            <span className="transition-transform group-open:rotate-90" aria-hidden>›</span>
            Toutes les dates sous forme de liste
          </summary>
        <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="dates-filtres">
          <button
            type="button"
            onClick={() => setTout(false)}
            data-testid="dates-filtre-a-venir"
            className={`pill ${tout ? "" : "active"}`}
          >
            À venir ({prochains.length})
          </button>
          <button
            type="button"
            onClick={() => setTout(true)}
            data-testid="dates-filtre-tout"
            className={`pill ${tout ? "active" : ""}`}
          >
            Toute la saison ({tous.length})
          </button>
        </div>
        <section className="mt-8 relative" data-testid="dates-timeline">
          <div
            className="absolute left-[19px] top-2 bottom-2 w-px hidden sm:block"
            style={{ background: "var(--border-soft)" }}
            aria-hidden
          />
          <ul className="space-y-4">
            {liste.map((e) => {
              const t = TYPES_AGENDA[e.type];
              const Icone = ICONES[e.type] || CalendarDays;
              return (
                <li key={e.id} className="relative sm:pl-14" data-testid={`date-${e.id}`}>
                  <span
                    className="absolute left-0 top-4 w-10 h-10 rounded-full hidden sm:flex items-center justify-center"
                    style={{ background: "#fff", border: `2px solid ${t.couleur}`, color: t.couleur }}
                    aria-hidden
                  >
                    <Icone size={18} />
                  </span>
                  <div className="card-flat">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span
                        className="text-[11px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full"
                        style={{ background: `${t.couleur}18`, color: t.couleur }}
                      >
                        {t.label}
                      </span>
                      <span className="text-sm font-medium">{periodeLisible(e.debut, e.fin)}</span>
                    </div>
                    <div className="font-display text-xl leading-tight">{e.titre}</div>
                    {e.detail && <p className="text-sm text-[var(--muted)] mt-2 leading-relaxed">{e.detail}</p>}
                    {e.lien && (
                      <Link
                        to={e.lien.to}
                        data-testid={`date-${e.id}-lien`}
                        className="inline-flex items-center gap-1 text-sm font-medium mt-3"
                        style={{ color: "var(--primary)" }}
                      >
                        {e.lien.label} <ArrowRight size={14} />
                      </Link>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
          {liste.length === 0 && (
            <p className="text-[var(--muted)]" data-testid="dates-vide">
              Aucune date à afficher pour le moment — les prochaines sont publiées dès que l'école
              les confirme.
            </p>
          )}
        </section>
        </details>

        <section className="mt-16 card-flat" data-testid="dates-liens">
          <div className="font-display text-2xl mb-3">Aller plus loin</div>
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
            <Link to="/planning" className="underline" style={{ color: "var(--primary)" }}>Planning hebdomadaire</Link>
            <Link to="/portes-ouvertes" className="underline" style={{ color: "var(--primary)" }}>Portes Ouvertes</Link>
            <Link to="/stages" className="underline" style={{ color: "var(--primary)" }}>Stages de vacances</Link>
            <Link to="/spectacles" className="underline" style={{ color: "var(--primary)" }}>Spectacles</Link>
            <Link to="/inscription" className="underline" style={{ color: "var(--primary)" }}>Inscriptions</Link>
          </div>
        </section>
      </div>
    </div>
  );
}
